import { useCallback, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  InstallRetryResult,
  InstallState,
} from '@volley/crucible-ipc-types/ipc-channels';

export function installStateQueryKey(gameId: string): readonly ['install-state', string] {
  return ['install-state', gameId] as const;
}

export interface InstallStateForGame {
  data: InstallState | null | undefined;
  isLoading: boolean;
  isRetrying: boolean;
  retryError: string | null;
  retryInstall: () => Promise<InstallRetryResult>;
  refresh: () => Promise<void>;
}

/**
 * Shared renderer owner for a game's persisted install-state record.
 *
 * Dashboard chips can keep their compact local surface, while Workspace can
 * compose install retry with preview start without letting a second retry state
 * hide inside `<InstallStateChip>`.
 */
export function useInstallStateForGame(
  gameId: string | undefined,
  options: { enabled?: boolean } = {},
): InstallStateForGame {
  const queryClient = useQueryClient();
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  // `retrying` / query-cache status lag by a render cycle; two same-tick
  // `retryInstall()` calls (combined retry+preview CTA) can both pass a
  // state-based guard. Ref is written synchronously before setState.
  const inFlightRef = useRef(false);
  const enabled = (options.enabled ?? true) && !!gameId;

  const query = useQuery({
    queryKey: gameId ? installStateQueryKey(gameId) : ['install-state', ''],
    queryFn: () => window.crucible.invoke('install-state:get', { gameId: gameId ?? '' }),
    enabled,
    staleTime: 5_000,
    refetchOnMount: true,
    retry: false,
  });

  const refresh = useCallback(async () => {
    if (!gameId) return;
    await queryClient.invalidateQueries({ queryKey: installStateQueryKey(gameId) });
  }, [gameId, queryClient]);

  const retryInstall = useCallback(async (): Promise<InstallRetryResult> => {
    if (!gameId) {
      return { ok: false, reason: 'No game loaded.' };
    }
    if (inFlightRef.current) {
      return { ok: false, reason: 'Install retry already running.' };
    }
    const queryKey = installStateQueryKey(gameId);
    const existing = queryClient.getQueryData<InstallState>(queryKey);
    if (existing?.status === 'installing') {
      return { ok: false, reason: 'Install retry already running.' };
    }

    inFlightRef.current = true;
    setRetrying(true);
    setRetryError(null);
    const now = new Date().toISOString();
    queryClient.setQueryData<InstallState>(queryKey, {
      status: 'installing',
      lastUpdate: now,
      startedAt: now,
    });

    try {
      const result = await window.crucible.invoke('install-state:retry', {
        gameId,
      });
      if (result.ok) {
        queryClient.setQueryData<InstallState>(queryKey, {
          status: 'ok',
          lastUpdate: new Date().toISOString(),
        });
      } else if ('alreadyInstalling' in result && result.alreadyInstalling) {
        queryClient.setQueryData<InstallState>(queryKey, {
          status: 'installing',
          reason: result.reason,
          lastUpdate: new Date().toISOString(),
          pid: result.pid,
          startedAt: result.startedAt,
        });
        setRetryError(result.reason);
      } else {
        queryClient.setQueryData<InstallState>(queryKey, {
          status: 'failed',
          reason: result.reason,
          occurredAt: new Date().toISOString(),
          lastUpdate: new Date().toISOString(),
        });
        setRetryError(result.reason);
      }
      await queryClient.invalidateQueries({ queryKey });
      return result;
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      queryClient.setQueryData<InstallState>(queryKey, {
        status: 'failed',
        reason,
        occurredAt: new Date().toISOString(),
        lastUpdate: new Date().toISOString(),
      });
      setRetryError(reason);
      await queryClient.invalidateQueries({ queryKey });
      return { ok: false, reason };
    } finally {
      inFlightRef.current = false;
      setRetrying(false);
    }
  }, [gameId, queryClient]);

  return {
    data: query.data,
    isLoading: query.isLoading || query.isFetching,
    isRetrying: retrying,
    retryError,
    retryInstall,
    refresh,
  };
}
